import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  cursosUteis,
  descricaoDoTema,
  enderecoDoLinkedin,
  experienciasUteis,
  formacoesUteis,
  idiomasUteis,
  linhasDe,
  type Curriculo,
  type DescricaoDoTema,
  type Secao,
} from "../lib/curriculoModelo";

// A FOLHA: o currículo do aluno desenhado, nos cinco temas.
//
// ---------------------------------------------------------------------------
// Por que aqui a cor é CRAVADA, ao contrário do resto do hub
// ---------------------------------------------------------------------------
// A regra da casa é cor semântica por token, porque a tela troca de tema. Esta
// folha é a exceção nomeada, pela mesma razão dos documentos do Simulador MEI:
// **ela é papel**. O currículo vai ser impresso, salvo em PDF e mandado para um
// recrutador — o fundo é branco nas duas pontas, e o navy do tema clássico é
// navy no escuro também. Cor semântica aqui faria o documento mudar de aparência
// conforme o tema do NAVEGADOR de quem edita, o que é absurdo.
//
// A moldura em volta (o editor, o painel, os botões) usa tokens normalmente.
//
// ---------------------------------------------------------------------------
// Um componente, três layouts, cinco temas
// ---------------------------------------------------------------------------
// O app de origem tinha cinco componentes de ~110 linhas cada, quase iguais —
// e foi essa duplicação que permitiu o nome do tema morar em dois lugares e o
// `default` do switch trocar o tema do aluno em silêncio. Aqui o que muda entre
// os temas está em `TEMAS` (`lib/curriculoModelo.ts`), e o que muda de verdade
// é o LAYOUT: uma coluna, barra lateral ou faixa.
//
// ---------------------------------------------------------------------------
// Zero marcação no texto do aluno
// ---------------------------------------------------------------------------
// Nada de `dangerouslySetInnerHTML`. Multilinha vira um `<p>` por linha, via
// `linhasDe` — que é como o app de origem também fazia, e é o certo: o aluno
// nunca digita marcação, então nunca há marcação para interpretar. O único valor
// que vira `href` é o LinkedIn, e ele é handle sobre prefixo fixo (000101).

const LARGURA_A4 = 794;

export function CurriculoFolha({
  c,
  /** `true` na prévia: a folha ocupa a largura disponível em vez dos 794px do A4. */
  fluida,
  id,
}: {
  c: Curriculo;
  fluida?: boolean;
  id?: string;
}) {
  const t = descricaoDoTema(c.tema);

  const base: CSSProperties = {
    width: fluida ? "100%" : `${LARGURA_A4}px`,
    minHeight: fluida ? undefined : "1123px",
    fontFamily: t.fonte,
    background: "#ffffff",
    color: t.corpo,
    // `translate="no"` porque tradutor automático de navegador reescreve o
    // currículo da pessoa — e o aluno não vê que aconteceu antes de imprimir.
  };

  const conteudo =
    t.layout === "barra-lateral" ? (
      <ComBarraLateral c={c} t={t} />
    ) : t.layout === "faixa" ? (
      <ComFaixa c={c} t={t} />
    ) : (
      <UmaColuna c={c} t={t} />
    );

  return (
    <div id={id} translate="no" lang="pt-BR" className="notranslate curriculo-folha" style={base}>
      {conteudo}
    </div>
  );
}

const ALTURA_A4 = 1123;

/**
 * A MINIATURA da folha: o currículo de verdade, escalado para caber numa caixa.
 *
 * É a peça que fez o painel do Gerador deixar de ser uma lista de linhas de
 * texto (redesenho de 26/08, a pedido de Victor). Um currículo É um documento —
 * e o aluno reconhece o dele pela cara, não pelo título interno "Meu currículo
 * (2)". Não é uma imagem estática nem um desenho genérico do tema: é o
 * `CurriculoFolha` real, com os dados do aluno, dentro de um `transform: scale`.
 * Trocar o tema na aba Modelo redesenha as cinco miniaturas com o texto dele.
 *
 * A escala sai da LARGURA da caixa (`ResizeObserver`), então a mesma peça serve
 * a um card de 260px e a uma galeria de 180px. A caixa tem a proporção do A4 e
 * corta o que passar de uma página — miniatura é para reconhecer, não para ler.
 *
 * `inert`: a folha tem um `<a>` (o LinkedIn) e vários textos. Dentro de uma
 * miniatura eles seriam alvo de Tab e de leitor de tela — 27 paradas de foco
 * numa galeria de cinco temas. `inert` tira a subárvore do foco e da árvore de
 * acessibilidade de uma vez; quem nomeia o card é o título ao lado. E é também
 * o que permite a miniatura ficar DENTRO de um card que tem um link, sem
 * aninhar `<a>` em `<a>`.
 */
export function MiniaturaDaFolha({ c, className = "" }: { c: Curriculo; className?: string }) {
  const caixa = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(0.3);

  useEffect(() => {
    const el = caixa.current;
    if (!el) return;
    const medir = () => {
      if (el.clientWidth > 0) setEscala(el.clientWidth / LARGURA_A4);
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={caixa}
      inert
      aria-hidden="true"
      data-miniatura={c.tema}
      className={`relative overflow-hidden bg-white ${className}`}
      style={{ aspectRatio: `${LARGURA_A4} / ${ALTURA_A4}` }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: LARGURA_A4,
          height: ALTURA_A4,
          transform: `scale(${escala})`,
          transformOrigin: "top left",
        }}
      >
        <CurriculoFolha c={c} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------------

function tituloDaSecao(t: DescricaoDoTema): CSSProperties {
  return {
    fontSize: t.tituloTamanho,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: t.tituloEspacamento,
    color: t.tinta,
    borderBottom: t.filete || undefined,
    paddingBottom: t.filete ? 4 : 0,
    marginBottom: 10,
    marginTop: 16,
  };
}

function Secao({
  t,
  titulo,
  children,
  marca,
}: {
  t: DescricaoDoTema;
  titulo: string;
  children: ReactNode;
  marca: string;
}) {
  return (
    // `break-inside: avoid` no título junto do primeiro item evita o título
    // sozinho no fim da página — o defeito clássico de currículo impresso.
    <section data-secao={marca} style={{ breakInside: "auto" }}>
      <h2 style={tituloDaSecao(t)}>{titulo}</h2>
      {children}
    </section>
  );
}

function Cabecalho({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  const link = enderecoDoLinkedin(c.linkedin);
  const contatos: ReactNode[] = [];
  for (const v of [c.localidade, c.telefone, c.email]) {
    if ((v ?? "").trim() !== "") contatos.push(v);
  }
  if (link !== "") {
    contatos.push(
      <a
        key="li"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        data-linkedin
        style={{ color: t.destaque || t.tinta, textDecoration: "underline" }}
      >
        LinkedIn
      </a>,
    );
  }

  return (
    <div data-secao="cabecalho" style={{ textAlign: t.cabecalhoCentralizado ? "center" : "left", marginBottom: 6 }}>
      <h1
        style={{
          fontSize: t.nomeTamanho,
          fontWeight: t.nomePeso,
          color: t.tinta,
          textTransform: t.nomeCaixaAlta ? "uppercase" : "none",
          letterSpacing: t.nomeCaixaAlta ? 3 : 0,
          marginBottom: 6,
          lineHeight: 1.15,
        }}
      >
        {c.nome.trim() || "Seu nome"}
      </h1>
      {contatos.length > 0 && (
        <p style={{ fontSize: 10, color: t.suave }}>
          {contatos.map((item, i) => (
            <span key={i}>
              {i > 0 && "  •  "}
              {item}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

function Experiencias({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  const itens = experienciasUteis(c.experiencias);
  if (itens.length === 0) return null;
  return (
    <Secao t={t} titulo="Experiência Profissional" marca="experiencias">
      {itens.map((e, i) => (
        <div key={i} data-item={`experiencia-${i}`} style={{ marginBottom: 12, breakInside: "avoid" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontSize: 11 }}>
              <strong>{t.gritaOrganizacao ? e.empresa.toUpperCase() : e.empresa}</strong>
              {e.cargo ? ` | ` : ""}
              {e.cargo && (
                <span style={{ fontStyle: t.italicoNoSecundario ? "italic" : "normal" }}>{e.cargo}</span>
              )}
              {e.local ? ` – ${e.local}` : ""}
            </span>
            <span style={{ fontSize: 9, color: t.suave, flexShrink: 0, fontStyle: t.italicoNoSecundario ? "italic" : "normal" }}>
              {e.periodo}
            </span>
          </div>
          {linhasDe(e.descricao).map((d, j) => (
            <p key={j} style={{ fontSize: 9, marginTop: j === 0 ? 3 : 0, marginBottom: 2, lineHeight: 1.5, color: t.corpo }}>
              • {d}
            </p>
          ))}
        </div>
      ))}
    </Secao>
  );
}

function Formacoes({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  const itens = formacoesUteis(c.formacoes);
  if (itens.length === 0) return null;
  return (
    <Secao t={t} titulo="Formação Acadêmica" marca="formacoes">
      {itens.map((f, i) => (
        <div key={i} data-item={`formacao-${i}`} style={{ marginBottom: 10, breakInside: "avoid" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontSize: 11 }}>
              <strong>{t.gritaOrganizacao ? f.instituicao.toUpperCase() : f.instituicao}</strong>
              {/* `titulo` e `curso` são campos DISTINTOS: dos 1.003 itens de
                  formação medidos, 898 têm os dois. Mas 105 têm só um, e a
                  separação com ": " só entra quando os dois existem. */}
              {f.titulo && f.curso ? ` | ${f.titulo}: ${f.curso}` : f.titulo || f.curso ? ` | ${f.titulo || f.curso}` : ""}
              {f.local ? ` – ${f.local}` : ""}
            </span>
            <span style={{ fontSize: 9, color: t.suave, flexShrink: 0, fontStyle: t.italicoNoSecundario ? "italic" : "normal" }}>
              {f.periodo}
            </span>
          </div>
        </div>
      ))}
    </Secao>
  );
}

function Habilidades({ c, t, emColuna }: { c: Curriculo; t: DescricaoDoTema; emColuna?: boolean }) {
  const itens = linhasDe(c.habilidades);
  if (itens.length === 0) return null;
  // O minimalista junta tudo numa linha com " · ", que é o desenho dele.
  if (t.id === "minimalista") {
    return (
      <Secao t={t} titulo="Habilidades e Competências" marca="habilidades">
        <p style={{ fontSize: 10, lineHeight: 1.7, color: t.corpo }}>{itens.join("  ·  ")}</p>
      </Secao>
    );
  }
  return (
    <Secao t={t} titulo="Habilidades e Competências" marca="habilidades">
      <div
        style={
          emColuna
            ? undefined
            : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 24px" }
        }
      >
        {itens.map((h, i) => (
          <p key={i} style={{ fontSize: 9, lineHeight: 1.6, color: t.corpo }}>
            • {h}
          </p>
        ))}
      </div>
    </Secao>
  );
}

function Idiomas({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  const itens = idiomasUteis(c.idiomas);
  if (itens.length === 0) return null;
  return (
    <Secao t={t} titulo="Idiomas" marca="idiomas">
      {itens.map((i, k) => (
        <p key={k} style={{ fontSize: 10, marginBottom: 3, color: t.corpo }}>
          • <strong>{i.nome}</strong>
          {i.nivel ? ` – ${i.nivel}` : ""}
        </p>
      ))}
    </Secao>
  );
}

function Cursos({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  const itens = cursosUteis(c.cursos);
  if (itens.length === 0) return null;
  return (
    <Secao t={t} titulo="Cursos Complementares" marca="cursos">
      {itens.map((cu, i) => (
        <p key={i} style={{ fontSize: 10, marginBottom: 3, color: t.corpo, breakInside: "avoid" }}>
          • <strong>{cu.nome}</strong>
          {cu.instituicao ? ` – ${cu.instituicao}` : ""}
        </p>
      ))}
    </Secao>
  );
}

function Resumo({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  if (c.resumo.trim() === "") return null;
  return (
    <Secao t={t} titulo="Resumo Profissional" marca="resumo">
      <p
        style={{
          fontSize: 10,
          lineHeight: 1.6,
          color: t.corpo,
          textAlign: "justify",
          fontStyle: t.italicoNoSecundario ? "italic" : "normal",
        }}
      >
        {c.resumo}
      </p>
    </Secao>
  );
}

function Informacoes({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  const itens = linhasDe(c.informacoes_adicionais);
  if (itens.length === 0) return null;
  return (
    <Secao t={t} titulo="Informações Adicionais" marca="informacoes">
      {itens.map((info, i) => (
        <p key={i} style={{ fontSize: 9, marginBottom: 3, lineHeight: 1.5, color: t.corpo }}>
          • {info}
        </p>
      ))}
    </Secao>
  );
}

// ---------------------------------------------------------------------------
// Os três layouts
// ---------------------------------------------------------------------------

function UmaColuna({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  return (
    <div style={{ padding: t.recheio }}>
      {t.filete === "" && t.id === "minimalista" ? (
        <>
          <Cabecalho c={c} t={t} />
          <div style={{ borderBottom: "1px solid #e5e5e5", marginTop: 14, marginBottom: 4 }} />
        </>
      ) : (
        <Cabecalho c={c} t={t} />
      )}
      <Resumo c={c} t={t} />
      <Experiencias c={c} t={t} />
      <Formacoes c={c} t={t} />
      <Habilidades c={c} t={t} />
      <Idiomas c={c} t={t} />
      <Cursos c={c} t={t} />
      <Informacoes c={c} t={t} />
    </div>
  );
}

function ComFaixa({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "14px 1fr", minHeight: "inherit" }}>
      <div style={{ background: t.tinta }} aria-hidden="true" />
      <div style={{ padding: t.recheio }}>
        <Cabecalho c={c} t={t} />
        <Resumo c={c} t={t} />
        <Experiencias c={c} t={t} />
        <Formacoes c={c} t={t} />
        <Habilidades c={c} t={t} />
        <Idiomas c={c} t={t} />
        <Cursos c={c} t={t} />
        <Informacoes c={c} t={t} />
      </div>
    </div>
  );
}

function ComBarraLateral({ c, t }: { c: Curriculo; t: DescricaoDoTema }) {
  const naBarra = (s: Secao) => t.naBarra.includes(s);
  const escuraNaBarra = t.id === "moderno";

  // No moderno a barra é navy com texto claro; no criativo é cinza-claro com o
  // texto normal. São dois desenhos, não um com parâmetro — e as cores da barra
  // escura precisam de contraste próprio, que é a mesma razão de `navy-mute`
  // existir no design system do hub.
  const barra: DescricaoDoTema = escuraNaBarra
    ? { ...t, tinta: "#ffffff", corpo: "#e6ecf2", suave: "#c2cede", filete: "1px solid rgba(255,255,255,0.35)" }
    : { ...t, filete: t.filete || "1px solid #e5e7eb" };

  return (
    <div>
      {t.resumoNoCabecalho && (
        <div
          data-secao="faixa-do-topo"
          style={{
            background: "#fff7ed",
            borderBottom: `3px solid ${t.destaque}`,
            padding: "26px 32px",
          }}
        >
          <Cabecalho c={c} t={t} />
          {c.resumo.trim() !== "" && (
            <p style={{ fontSize: 10, lineHeight: 1.6, color: t.corpo, marginTop: 8, textAlign: "justify" }}>
              {c.resumo}
            </p>
          )}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: escuraNaBarra ? "260px 1fr" : "230px 1fr", minHeight: "inherit" }}>
        <aside
          style={{
            background: escuraNaBarra ? t.tinta : "#fafafa",
            borderRight: escuraNaBarra ? undefined : "1px solid #eeeeee",
            padding: "26px 22px",
          }}
        >
          {!t.resumoNoCabecalho && <Cabecalho c={c} t={barra} />}
          {naBarra("habilidades") && <Habilidades c={c} t={barra} emColuna />}
          {naBarra("idiomas") && <Idiomas c={c} t={barra} />}
          {naBarra("cursos") && <Cursos c={c} t={barra} />}
        </aside>
        <div style={{ padding: "26px 30px" }}>
          {!t.resumoNoCabecalho && <Resumo c={c} t={t} />}
          <Experiencias c={c} t={t} />
          <Formacoes c={c} t={t} />
          {!naBarra("habilidades") && <Habilidades c={c} t={t} />}
          {!naBarra("idiomas") && <Idiomas c={c} t={t} />}
          {!naBarra("cursos") && <Cursos c={c} t={t} />}
          <Informacoes c={c} t={t} />
        </div>
      </div>
    </div>
  );
}
